<template>
  <div class="room-type-competitor-mapping">
    <!-- Header Actions -->
    <v-row class="mb-4">
      <v-col class="d-flex align-center">
        <h2 class="text-h6 pr-2">{{ $t("competitor_room_mapping") }}</h2>
        <a-display-options-container>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                small
                :color="!compactView ? 'secondary' : 'grey'"
                :class="!compactView ? 'active' : ''"
                @click="compactView = false"
              >
                mdi-server-outline
              </v-icon>
            </template>
            <span>{{ $t("expanded") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                small
                :color="compactView ? 'secondary' : 'grey'"
                :class="compactView ? 'active' : ''"
                @click="compactView = true"
              >
                mdi-format-align-justify
              </v-icon>
            </template>
            <span>{{ $t("compact") }}</span>
          </v-tooltip>
        </a-display-options-container>
      </v-col>
      <v-col class="d-flex align-center justify-end" style="gap: 12px">
        <v-btn color="secondary" rounded @click="showConfigModal = true">
          <v-icon left>mdi-cog</v-icon>
          {{ $t("modify_columns") }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Mapping Matrix -->
    <div class="mapping-container" v-if="!loading">
      <table class="mapping-table">
        <thead>
          <tr>
            <th class="competitor-header">{{ $t("competitor") }}</th>
            <th class="unclassified-header">{{ $t("unclassified") }}</th>
            <th v-for="col in columns" :key="col.id" class="category-header">
              {{ col.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="competitor in competitors" :key="competitor.id">
            <!-- Competitor Name -->
            <td class="competitor-cell">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-on="on"
                    v-bind="attrs"
                    class="competitor-name font-weight-bold text-truncate"
                  >
                    {{ competitor.name }}
                  </div>
                </template>
                <span>{{ competitor.name }}</span>
              </v-tooltip>
              <div v-if="competitor.is_current" class="caption primary--text">
                ({{ $t("my_hotel") }})
              </div>
            </td>

            <!-- Unclassified Column -->
            <td
              class="drop-zone unclassified-zone"
              :data-competitor-id="competitor.id"
              data-category="unclassified"
            >
              <a-room-type-card
                v-for="room in getRooms(competitor.id, 'unclassified')"
                :key="room.id"
                :room="room"
                :heatmap-style="getHeatmapStyle(room)"
                :color="getHeatmapColor(room)"
                :currency-symbol="currencySymbol"
                :compact="compactView"
              />
            </td>

            <!-- Dynamic Category Columns -->
            <td
              v-for="col in columns"
              :key="col.id"
              class="drop-zone category-zone"
              :data-competitor-id="competitor.id"
              :data-category="col.id"
            >
              <a-room-type-card
                v-for="room in getRooms(competitor.id, col.id)"
                :key="room.id"
                :room="room"
                :heatmap-style="getHeatmapStyle(room)"
                :color="getHeatmapColor(room)"
                :currency-symbol="currencySymbol"
                :compact="compactView"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-skeleton-loader v-else type="table"></v-skeleton-loader>

    <!-- Config Modal -->
    <a-room-type-config-modal
      v-model="showConfigModal"
      :initial-selected-types="allMappings"
      :hotel-id="current_hotel.id"
      @save="updateColumns"
      @mapping-changed="onMappingChanged"
    />
  </div>
</template>

<script>
import RoomTypeService from "@/services/RoomTypeService";
import Sortable from "sortablejs";
import { mapGetters } from "vuex";
import aRoomTypeCard from "./aRoomTypeCard.vue";
import aRoomTypeConfigModal from "./aRoomTypeConfigModal.vue";
import aDisplayOptionsContainer from "@/components/aDisplayOptionsContainer";

export default {
  name: "aRoomTypeCompetitorMapping",
  components: {
    aRoomTypeConfigModal,
    aRoomTypeCard,
    aDisplayOptionsContainer
  },
  data() {
    return {
      loading: false,
      showConfigModal: false,
      compactView: false,
      // All mappings from backend (visible + hidden)
      allMappings: [],
      // Only visible columns for the table display
      columns: [],
      competitors: [],
      // Flat list of all rooms with their mapping state
      // Structure: { id: 1, name: "Deluxe", price: 100, competitor_id: 1, category_id: 'double' | 'unclassified' }
      allRooms: [],
      sortableInstances: []
    };
  },
  computed: {
    ...mapGetters(["current_hotel", "current_hotel_language"]),
    currencySymbol() {
      return this.current_hotel_language
        ? this.current_hotel_language.money_post_symbol
        : "€";
    },
    // Calculate price range PER COMPETITOR for heatmap coloring
    competitorPriceRanges() {
      const ranges = {};

      this.competitors.forEach(comp => {
        const compRooms = this.allRooms.filter(
          r => r.competitor_id == comp.id && r.price_avg != null
        );
        const avgPrices = compRooms.map(r => r.price_avg);

        if (avgPrices.length < 2) {
          ranges[comp.id] = null;
        } else {
          ranges[comp.id] = {
            min: Math.min(...avgPrices),
            max: Math.max(...avgPrices)
          };
        }
      });

      return ranges;
    }
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    this.destroySortables();
  },
  methods: {
    async fetchData() {
      if (!this.current_hotel) return;

      this.loading = true;
      try {
        // Fetch config
        try {
          const configResponse = await RoomTypeService.getRoomTypeMappings(
            this.current_hotel.id
          );
          // Check for 'mappings' instead of 'columns'
          if (
            configResponse.data &&
            configResponse.data.mappings &&
            configResponse.data.mappings.length > 0
          ) {
            // Store ALL mappings for the modal
            this.allMappings = configResponse.data.mappings;
            // Only show visible columns in the table
            this.columns = configResponse.data.mappings.filter(m => m.visible);
          }
        } catch (e) {
          //console.warn("Could not fetch mapping config", e);
        }

        // Fetch competitors
        const response = await RoomTypeService.getCompsetRoomTypesForMapping(
          this.current_hotel.id
        );
        let data =
          response.data && response.data.competitors
            ? response.data.competitors
            : [];

        this.competitors = data.map(c => ({
          id: c.id,
          name: c.name,
          is_current: c.self
        }));

        // Process rooms into flat structure
        this.allRooms = [];
        data.forEach(comp => {
          if (comp.room_types) {
            comp.room_types.forEach(room => {
              // Determine category based on room_type_mapping_id
              let categoryId = "unclassified";
              if (room.room_type_mapping_id) {
                // Check if this mapping ID exists in our columns
                if (
                  this.columns.some(col => col.id === room.room_type_mapping_id)
                ) {
                  categoryId = room.room_type_mapping_id;
                }
              }

              // Calculate average price from min/max
              const priceMin =
                room.price_min != null ? parseFloat(room.price_min) : null;
              const priceMax =
                room.price_max != null ? parseFloat(room.price_max) : null;
              const priceAvg =
                room.price_avg != null ? parseFloat(room.price_avg) : null;

              this.allRooms.push({
                ...room,
                id: room.ota_room_id,
                name: room.room_name,
                competitor_id: comp.id,
                category_id: categoryId,
                booking_room_id: room.ota_room_id,
                price_min: priceMin,
                price_max: priceMax,
                price_avg: priceAvg
              });
            });
          }
        });

        this.loading = false;
        this.$nextTick(() => {
          this.initSortables();
        });
      } catch (error) {
        //console.error("Error fetching competitors", error);
        this.loading = false;
      }
    },
    getRooms(competitorId, categoryId) {
      return this.allRooms.filter(
        r => r.competitor_id == competitorId && r.category_id == categoryId
      );
    },
    updateColumns(newColumns) {
      // This is called when the modal is saved/closed
      // Re-fetch to ensure sync with backend state
      this.fetchData();
    },
    onMappingChanged() {
      // This is called when any mapping is changed in the modal (add, delete, toggle visibility, reorder)
      // Re-fetch to keep the table in sync
      this.fetchData();
    },
    initSortables() {
      this.destroySortables();

      const zones = document.querySelectorAll(".drop-zone");
      zones.forEach(el => {
        const sortable = Sortable.create(el, {
          group: {
            name: "rooms-" + el.dataset.competitorId,
            pull: true,
            put: (to, from) => {
              // Double check: ensure we are dropping into the same competitor row
              return (
                to.el.dataset.competitorId === from.el.dataset.competitorId
              );
            }
          },
          animation: 150,
          ghostClass: "sortable-ghost",
          chosenClass: "sortable-chosen",
          dragClass: "sortable-drag",
          sort: false, // We don't care about order within cell for now, just grouping
          onAdd: evt => {
            this.handleDrop(evt);
          },
          onRemove: evt => {
            // Handled by onAdd in target
          }
        });
        this.sortableInstances.push(sortable);
      });
    },
    destroySortables() {
      this.sortableInstances.forEach(s => s.destroy());
      this.sortableInstances = [];
    },
    handleDrop(evt) {
      const roomId = evt.item.dataset.id;
      const targetCategory = evt.to.dataset.category;

      // IMPORTANT: Revert DOM change made by SortableJS
      // We let Vue handle the rendering based on data
      const item = evt.item;
      const from = evt.from;
      if (evt.oldIndex !== undefined) {
        // Move the element back to its original container
        from.insertBefore(item, from.children[evt.oldIndex] || null);
      }

      // Update data model - Vue will re-render correctly
      const room = this.allRooms.find(r => r.id == roomId);
      if (room) {
        const oldCategory = room.category_id;
        room.category_id = targetCategory;

        if (targetCategory === "unclassified") {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("unassigning_room"),
            color: "secondary"
          });

          RoomTypeService.unassignRoomTypeToMapping({
            hotel_id: this.current_hotel.id,
            competitor_id: room.competitor_id,
            ota_type: room.ota_type,
            ota_room_id: room.ota_room_id
          })
            .then(() => {
              this.$store.dispatch("setSnackbar", {
                title: this.$t("room_unassigned"),
                color: "accent"
              });
            })
            .catch(err => {
              //console.error("Error unassigning room", err);
              room.category_id = oldCategory;
              this.$store.dispatch("setSnackbar", {
                title: this.$t("room_not_unassigned"),
                color: "error"
              });
            });
        } else {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("assigning_room"),
            color: "secondary"
          });

          RoomTypeService.assignRoomTypeToMapping({
            hotel_id: this.current_hotel.id,
            room_type_mapping_id: targetCategory,
            competitor_id: room.competitor_id,
            ota_type: room.ota_type,
            ota_room_id: room.ota_room_id
          })
            .then(() => {
              this.$store.dispatch("setSnackbar", {
                title: this.$t("room_assigned"),
                color: "accent"
              });
            })
            .catch(err => {
              //console.error("Error assigning room", err);
              room.category_id = oldCategory;
              this.$store.dispatch("setSnackbar", {
                title: this.$t("room_not_assigned"),
                color: "error"
              });
            });
        }
      }
    },
    getHeatmapColor(room) {
      if (room.price_avg == null) return null;

      const range = this.competitorPriceRanges[room.competitor_id];
      if (!range) return null;

      const { min, max } = range;

      if (min === max) return null;

      // Normalize price between 0 and 1 based on COMPETITOR range
      // 0 (min) -> Green (lower prices)
      // 1 (max) -> Red (higher prices)
      const ratio = (room.price_avg - min) / (max - min);

      // Green: rgb(76, 175, 80)
      // Red: rgb(244, 67, 54)
      const r = Math.round(76 + (244 - 76) * ratio);
      const g = Math.round(175 + (67 - 175) * ratio);
      const b = Math.round(80 + (54 - 80) * ratio);

      return `rgb(${r}, ${g}, ${b})`;
    },
    getHeatmapStyle(room) {
      const color = this.getHeatmapColor(room);
      if (!color) return {};

      return {
        backgroundColor: color.replace("rgb", "rgba").replace(")", ", 0.2)"),
        border: `1px solid ${color}`
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.mapping-container {
  overflow-x: auto;
}
.mapping-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}
.mapping-table th,
.mapping-table td {
  border: 1px solid #e0e0e0;
  padding: 8px;
  vertical-align: top;
}
.mapping-table th {
  font-size: 14px;
}
.competitor-header {
  background-color: #f5f5f5;
  width: 200px;
}
.unclassified-header {
  background-color: #f5f5f5;
  width: 200px;
}
.category-header {
  min-width: 150px;
  background-color: #f9f9f9;
  text-align: center;
}
.competitor-cell {
  background-color: #e5e5e5;
  max-width: 200px;
  .competitor-name {
    font-size: 0.8rem !important;
    line-height: 1;
    letter-spacing: 0;
    color: #555;
  }
}
.drop-zone {
  min-height: 60px;
  background-color: #fafafa;
  transition: background-color 0.2s;
}
.drop-zone.unclassified-zone {
  background-color: #f3f3f3;
}
.sortable-ghost {
  border-color: transparent !important;
  box-shadow: none !important;
  position: relative;
  opacity: 1 !important;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #e1f5fe;
    border: 2px dashed #3498db;
    border-radius: 4px;
    z-index: 10;
  }
}
.sortable-chosen {
  background-color: #e3f2fd !important;
}
.sortable-drag {
  opacity: 1 !important;
  background-color: #ffffff !important;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15) !important;
  z-index: 9999 !important;
}
</style>
